/* SILKhee METALUX — metallic page, v5 — poly-wall purchase block, tower as the hero preview, 2026-09-11.
   The poly wall's grid + chip accordion + info panel, verbatim in behaviour; the tower is a
   preview/jump navigator only (one slat per metal). One sticky stage (3D cone + selected shade) on desktop, a bottom
   sheet on phones. Loaded as a module on the metallic template only. Cone geometry lifted
   from silkhee-wall-v30.js (same object customers see on the poly wall); the material is
   not — metallic film reads as brushed metal, so real metalness over the strand texture plus
   a small procedural studio environment to reflect. Names-forward: nothing here writes the
   STV code into visible text. Cart: /cart/add.js with window.SILKhee.variants[sku] from
   assets/silkhee-metallic-map.js. Votes: Formspree xbdqraql, same as browse-all-colors. */
import * as THREE from 'three';

const CFG = SKUnpack(document.getElementById('metallic-config').textContent);
const LINE = CFG.line || 'METALUX';
const COLORS = CFG.colors;
const BY = {}; COLORS.forEach(c => { BY[c.code] = c; });
const root = document.getElementById('mx');
const stageEl = document.getElementById('mxCone');
const el = id => document.getElementById(id);
const isPhone = () => window.matchMedia('(max-width:900px)').matches;
const money = n => '$' + Number(n).toFixed(2);
const famOf = code => CFG.families.find(f => f.codes.includes(code));

/* availability: everything sells except 'later' (not yet wound for the US) — those vote.
   The lot-#2 shades sell at zero stock (variants set to CONTINUE); the map's number only
   gates the SET OF 6 row when we actually know the count. */
const sellable = c => c.avail !== 'later';
const stockOf = c => { const s = window.SILKhee && window.SILKhee.stock; return (s && typeof s[c.sku] === 'number') ? s[c.sku] : 0; };
const variantOf = c => window.SILKhee && window.SILKhee.variants && window.SILKhee.variants[c.sku];

/* ── 3D cone ───────────────────────────────────────────────────────── */
let renderer, scene, camera, cone, coneMaterial, envTex;
let rotX = 0, rotXVis = 0, dragging = false, lastDragEnd = 0;

function studioEnv(){
  if (envTex) return envTex;
  const c = document.createElement('canvas'); c.width = 512; c.height = 256;
  const g = c.getContext('2d');
  const sky = g.createLinearGradient(0, 0, 0, 256);
  sky.addColorStop(0, '#ffffff'); sky.addColorStop(.3, '#e9e9e6'); sky.addColorStop(.62, '#b9b9b4'); sky.addColorStop(1, '#5b5b58');
  g.fillStyle = sky; g.fillRect(0, 0, 512, 256);
  g.fillStyle = 'rgba(255,255,255,.9)'; g.fillRect(140, 18, 232, 40);
  const strip = (x) => { const s = g.createLinearGradient(x-22, 0, x+22, 0);
    s.addColorStop(0,'rgba(255,255,255,0)'); s.addColorStop(.5,'rgba(255,255,255,.55)'); s.addColorStop(1,'rgba(255,255,255,0)');
    g.fillStyle = s; g.fillRect(x-22, 40, 44, 150); };
  strip(96); strip(416);
  envTex = new THREE.CanvasTexture(c);
  envTex.mapping = THREE.EquirectangularReflectionMapping;
  envTex.colorSpace = THREE.SRGBColorSpace;
  return envTex;
}

function initCone(){
  const W = stageEl.clientWidth, H = stageEl.clientHeight;
  scene = new THREE.Scene();
  scene.environment = studioEnv();
  camera = new THREE.PerspectiveCamera(28, W/H, 0.1, 100);
  camera.position.set(0, 0.5, 8); camera.lookAt(0, 0, 0);
  renderer = new THREE.WebGLRenderer({antialias:true, alpha:true, premultipliedAlpha:false});
  renderer.setSize(W, H);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.22;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  stageEl.insertBefore(renderer.domElement, stageEl.firstChild);

  scene.add(new THREE.AmbientLight(0xffffff, 0.62));
  const key = new THREE.DirectionalLight(0xffffff, 1.15); key.position.set(3, 4, 5); scene.add(key);
  const fill = new THREE.DirectionalLight(0xfff5e8, 0.35); fill.position.set(-3, 2, 4); scene.add(fill);
  const rim = new THREE.DirectionalLight(0xffffff, 0.75); rim.position.set(-1, -1, -3); scene.add(rim);

  // Same lathe profile as the poly wall (v22 → v30)
  const profile = [
    [0.000,0.78],[0.012,0.81],[0.024,0.81],[0.040,0.79],
    [0.055,0.74],[0.070,0.69],[0.100,0.685],[0.180,0.683],
    [0.280,0.678],[0.380,0.672],[0.480,0.667],[0.580,0.661],
    [0.680,0.655],[0.760,0.651],[0.820,0.649],[0.860,0.652],
    [0.880,0.645],[0.905,0.585],[0.930,0.485],[0.950,0.380],
    [0.960,0.340],[0.975,0.310],[0.985,0.300],
  ];
  const sceneH = 3.0;
  const lathe = (pts, seg) => new THREE.LatheGeometry(pts.map(p => new THREE.Vector2(p[1], -sceneH/2 + p[0]*sceneH)), seg);
  const threadGeom = lathe(profile.filter(p => p[0] >= 0.07 && p[0] <= 0.96), 96);
  const flangeGeom = lathe(profile.filter(p => p[0] <= 0.07), 48);
  const capGeom    = lathe(profile.filter(p => p[0] >= 0.96), 32);

  // Strand texture — tighter, brighter strokes than poly: metallic film winds as fine bright lines
  const sc = document.createElement('canvas'); sc.width = 512; sc.height = 1024;
  const ctx = sc.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 512, 1024);
  for (let i = 0; i < 2200; i++){
    const x = Math.random() * 512, y = Math.random() * 1024;
    ctx.strokeStyle = `rgba(255,255,255,${0.05 + Math.random() * 0.08})`;
    ctx.lineWidth = 0.4 + Math.random() * 0.4;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 90 + Math.random() * 90, y + (Math.random() - 0.5) * 3); ctx.stroke();
  }
  const strandTex = new THREE.CanvasTexture(sc);
  strandTex.wrapS = THREE.RepeatWrapping; strandTex.wrapT = THREE.ClampToEdgeWrapping;
  strandTex.repeat.set(2, 1); strandTex.colorSpace = THREE.SRGBColorSpace;

  coneMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffffff, map: strandTex,
    roughness: 0.34, metalness: 0.72,
    clearcoat: 0.5, clearcoatRoughness: 0.2,
    sheen: 0.9, sheenColor: 0xffffff, sheenRoughness: 0.25,
    envMapIntensity: 1.6,
  });
  const plastic = new THREE.MeshStandardMaterial({color:'#f5f5f5', roughness:.25, metalness:0, side:THREE.DoubleSide});

  cone = new THREE.Group();
  cone.add(new THREE.Mesh(threadGeom, coneMaterial));
  cone.add(new THREE.Mesh(flangeGeom, plastic));
  cone.add(new THREE.Mesh(capGeom, plastic));
  cone.scale.setScalar(0.85);
  scene.add(cone);

  let px = 0, py = 0;
  const down = e => { dragging = true; px = e.clientX; py = e.clientY; stageEl.setPointerCapture && stageEl.setPointerCapture(e.pointerId); };
  const move = e => { if (!dragging) return; cone.rotation.y += (e.clientX - px) * 0.012; rotX += (e.clientY - py) * 0.006; rotX = Math.max(-0.6, Math.min(0.6, rotX)); px = e.clientX; py = e.clientY; };
  const up = () => { if (dragging){ dragging = false; lastDragEnd = performance.now(); } };
  stageEl.addEventListener('pointerdown', down); stageEl.addEventListener('pointermove', move);
  stageEl.addEventListener('pointerup', up); stageEl.addEventListener('pointercancel', up); stageEl.addEventListener('pointerleave', up);

  (function animate(){
    requestAnimationFrame(animate);
    const idle = !dragging && (lastDragEnd === 0 || performance.now() - lastDragEnd > 5000);
    if (idle){ cone.rotation.y += 0.003; rotX *= 0.94; }
    rotXVis += (rotX - rotXVis) * 0.18;
    cone.rotation.x = rotXVis;
    renderer.render(scene, camera);
  })();

  window.addEventListener('resize', () => {
    const w = stageEl.clientWidth, h = stageEl.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h); camera.aspect = w/h; camera.updateProjectionMatrix();
  }, { passive:true });
}

/* ── info panel (selected shade) ───────────────────────────────────── */
let CUR = null;
function paintStage(c){
  if (!coneMaterial) initCone();
  coneMaterial.color.set(c.hex);
  const fam = famOf(c.code);
  el('mxStLine').textContent = `${LINE} · ${fam ? fam.name : 'METALLIC'}`;
  el('mxStName').textContent = c.name;
  el('mxStSpec').textContent = `30WT · ${c.weight}G · ${c.silver ? 'REAL SILVER-WOUND FILM' : 'POLY CORE / METAL FILM'}`;
  el('mxStAg').hidden = !c.silver;
  el('mxBuyTitle').textContent = `${LINE} · ${c.name.toUpperCase()}`;
  const ok = sellable(c);
  el('mxAdd').hidden = !ok; el('mxVote').hidden = ok;
  el('mxAdd').textContent = `+ ADD TO CART — ${money(c.price)}`;
  el('mxAvail').textContent = ok ? 'FULL OPTIONS BELOW THE COLOR' : 'NOT YET WOUND FOR THE US · VOTE IT INTO THE NEXT RUN';
}
function select(code, open){
  const c = BY[code]; if (!c) return;
  CUR = c;
  root.querySelectorAll('.mx-card').forEach(b => b.classList.toggle('active', b.dataset.code === code));
  paintStage(c);
  if (open && isPhone()){ root.classList.add('mx-open'); document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => window.dispatchEvent(new Event('resize'))); }
  try { history.replaceState(null, '', '#' + code); } catch(_){}
}
function closeSheet(){ root.classList.remove('mx-open'); document.body.style.overflow = ''; }

/* 2026-09-16 · CIEDE2000 for CLOSEST WOUND. Metallic-to-metallic only (a metallic
   is never a substitute for a poly and a poly is never offered here). Ported
   verbatim from ~/Desktop/inject.js; duplicated in silkhee-wall-v30.js by
   design (avoids a layout/theme.liquid edit to load a shared asset). */
function hexToLab(hex){var h=hex.replace('#',''),r=parseInt(h.slice(0,2),16)/255,g=parseInt(h.slice(2,4),16)/255,b=parseInt(h.slice(4,6),16)/255;var lin=function(c){return c<=0.04045?c/12.92:Math.pow((c+0.055)/1.055,2.4)};r=lin(r);g=lin(g);b=lin(b);var X=r*0.4124564+g*0.3575761+b*0.1804375,Y=r*0.2126729+g*0.7151522+b*0.0721750,Z=r*0.0193339+g*0.1191920+b*0.9503041;var f=function(t){return t>0.008856?Math.cbrt(t):7.787*t+16/116};var fx=f(X/0.95047),fy=f(Y),fz=f(Z/1.08883);return[116*fy-16,500*(fx-fy),200*(fy-fz)]}
function dE(l1,l2){var L1=l1[0],a1=l1[1],b1=l1[2],L2=l2[0],a2=l2[1],b2=l2[2],rad=Math.PI/180,deg=180/Math.PI;var C1=Math.hypot(a1,b1),C2=Math.hypot(a2,b2),Cb=(C1+C2)/2,G=0.5*(1-Math.sqrt(Math.pow(Cb,7)/(Math.pow(Cb,7)+Math.pow(25,7))));var a1p=a1*(1+G),a2p=a2*(1+G),C1p=Math.hypot(a1p,b1),C2p=Math.hypot(a2p,b2);var hue=function(a,b){return(a||b)?((Math.atan2(b,a)*deg)+360)%360:0};var h1=hue(a1p,b1),h2=hue(a2p,b2),dL=L2-L1,dC=C2p-C1p,dh;if(C1p*C2p===0)dh=0;else if(Math.abs(h2-h1)<=180)dh=h2-h1;else dh=(h2-h1>180)?h2-h1-360:h2-h1+360;var dH=2*Math.sqrt(C1p*C2p)*Math.sin(dh/2*rad),Lb=(L1+L2)/2,Cbp=(C1p+C2p)/2,Hb;if(C1p*C2p===0)Hb=h1+h2;else if(Math.abs(h1-h2)<=180)Hb=(h1+h2)/2;else Hb=(h1+h2<360)?(h1+h2+360)/2:(h1+h2-360)/2;var T=1-0.17*Math.cos((Hb-30)*rad)+0.24*Math.cos(2*Hb*rad)+0.32*Math.cos((3*Hb+6)*rad)-0.20*Math.cos((4*Hb-63)*rad);var SL=1+0.015*Math.pow(Lb-50,2)/Math.sqrt(20+Math.pow(Lb-50,2)),SC=1+0.045*Cbp,SH=1+0.015*Cbp*T;var RT=-2*Math.sqrt(Math.pow(Cbp,7)/(Math.pow(Cbp,7)+Math.pow(25,7)))*Math.sin(60*Math.exp(-Math.pow((Hb-275)/25,2))*rad);return Math.sqrt(Math.pow(dL/SL,2)+Math.pow(dC/SC,2)+Math.pow(dH/SH,2)+RT*(dC/SC)*(dH/SH))}
function closestWound(target, N=4){
  const t = hexToLab(target.hex);
  return COLORS
    .filter(c => sellable(c) && c.code !== target.code)
    .map(c => ({ c, d: dE(t, hexToLab(c.hex)) }))
    .sort((a,b) => a.d - b.d)
    .slice(0, N)
    .map(x => x.c);
}

/* ── chip accordion — the poly wall's inline buy expansion below the clicked row ── */
let ddEl = null, ddCode = null;
const row = (cls, label, price, arrow, attrs) =>
  `<div class="mx-dd-row ${cls}" ${attrs || ''} role="button" tabindex="0">
     <span class="mx-dd-l">${label}</span><span class="mx-dd-p">${price}</span><span class="mx-dd-a">${arrow}</span>
   </div>`;
/* 2026-09-16 · rev 5: render a CLOSEST WOUND band that mirrors the tower's
   .mx-tw-b markup — same --h/--hi/--lo triplet lifted from the tower node so
   the atlas, tower and DD tint identically, name under it in the tower's mono
   caps (Names-forward: code lives on data-code only, never visible text). */
const nearBandHTML = (t) => {
  const twB = document.querySelector('.mx-tw-b[data-code="' + t.code + '"]');
  const style = twB ? (twB.getAttribute('style') || '') : '';
  return `<button type="button" class="mx-tw-b mx-tw-b--near" data-code="${t.code}" aria-label="${t.name}" style="${style}">
    <span class="mx-tw-foil"></span>
    <span class="mx-tw-b-name">${t.name}</span>
  </button>`;
};
function ddHTML(c){
  const fam = famOf(c.code);
  const head = `<div class="mx-dd-head"><span class="l">${LINE}</span><span class="s">·</span><span class="n">${c.name}</span>
      <button type="button" class="mx-dd-x" data-dd-close aria-label="Close">×</button></div>`;
  if (!sellable(c)){
    const near = closestWound(c, 4);
    const nearHTML = near.length
      ? `<div class="mx-dd-oos mx-dd-oos--label">CLOSEST WOUND</div>
         <div class="mx-dd-near">${near.map(nearBandHTML).join('')}</div>`
      : '';
    return `<div class="mx-dd-in mx-dd--oos">${head}
      <div class="mx-dd-oos">Not yet wound for the US · vote it into the next run</div>
      ${nearHTML}
      ${row('mx-dd-row--vote', 'VOTE FOR NEXT RUN ✳', '', '→', 'data-vote')}
      ${row('mx-dd-row--cone', 'SEE IT ON THE CONE', '', '→', 'data-cone')}
    </div>`;
  }
  const q = stockOf(c), six = q >= 6 || q === 0;
  return `<div class="mx-dd-in">${head}
    ${row('mx-dd-row--accent', 'ADD SINGLE', money(c.price), '→', 'data-qty="1"')}
    ${six ? row('', 'SET OF 6 · ONE PER HEAD', money(c.price * 6), '→', 'data-qty="6"')
          : row('mx-dd-row--sold', 'SET OF 6 · ONE PER HEAD', 'LOW STOCK', '', '')}
    ${row('', '12 + · STUDIO PRICING', 'REQUEST A QUOTE', '→', 'data-quote')}
    ${row('mx-dd-row--cone', 'SEE IT ON THE CONE', '', '→', 'data-cone')}
    <span class="mx-dd-foot">${fam ? fam.name : ''} · 30WT · ${c.weight}G · ${c.silver ? 'REAL SILVER-WOUND FILM' : 'POLY CORE / METAL FILM'}</span>
  </div>`;
}
function openDD(cardEl, c){
  closeDD();
  const grid = cardEl.closest('.mx-grid-inner'); if (!grid) return;
  const cards = Array.from(grid.querySelectorAll('.mx-card'));
  const idx = cards.indexOf(cardEl); if (idx < 0) return;
  const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(s => s.length).length || 8;
  const last = cards[Math.min((Math.floor(idx / cols) + 1) * cols - 1, cards.length - 1)];
  const wrap = document.createElement('div');
  wrap.className = 'mx-dd'; wrap.dataset.code = c.code; wrap.innerHTML = ddHTML(c);
  last.after(wrap); ddEl = wrap; ddCode = c.code;
  const wire = (sel, fn) => wrap.querySelectorAll(sel).forEach(r => {
    r.addEventListener('click', e => { e.stopPropagation(); fn(r); });
    r.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' '){ e.preventDefault(); fn(r); } });
  });
  wire('[data-dd-close]', closeDD);
  wire('[data-qty]', r => addToCart(c, parseInt(r.dataset.qty, 10), r.querySelector('.mx-dd-p')));
  wire('[data-quote]', () => { location.href = `mailto:hello@silkhee-us.com?subject=${encodeURIComponent(LINE + ' studio pricing — ' + c.name + ' (' + c.sku + ')')}`; });
  wire('[data-vote]', () => openVote(c));
  wire('[data-cone]', () => select(c.code, true));
  /* 2026-09-16 · rev 5: CLOSEST WOUND bands. Click any → open THAT shade's
     own drop-down against its card, exactly as clicking it on the tower does. */
  wrap.querySelectorAll('.mx-tw-b--near').forEach(nearEl => {
    nearEl.addEventListener('click', (e) => {
      e.stopPropagation();
      const code = nearEl.dataset.code, target = BY[code];
      const card = root.querySelector('.mx-card[data-code="' + code + '"]');
      if (!target || !card) return;
      select(code, false);
      const grp = card.closest('.mx-grp'); if (grp){ grp.style.scrollMarginTop = '68px'; grp.scrollIntoView({ block:'start' }); }
      openDD(card, target);
    });
  });
  /* 2026-09-16 · rev 5: fade the vote-only shade's tower band while its DD
     is open. Closed, it renders as today. */
  if (!sellable(c)){
    const twB = document.querySelector('.mx-tw-b[data-code="' + c.code + '"]');
    if (twB) twB.classList.add('mx-tw-b--voting');
  }
  requestAnimationFrame(() => wrap.classList.add('open'));
}
function closeDD(){
  if (ddEl){
    /* 2026-09-16 · rev 5: clear the vote-only fade on close. */
    const twB = ddCode ? document.querySelector('.mx-tw-b[data-code="' + ddCode + '"]') : null;
    if (twB) twB.classList.remove('mx-tw-b--voting');
    ddEl.remove(); ddEl = null; ddCode = null;
  }
}

root.querySelectorAll('.mx-card').forEach(b => b.addEventListener('click', () => {
  const code = b.dataset.code;
  if (ddCode === code){ select(code, false); closeDD(); return; }
  select(code, false);
  openDD(b, BY[code]);
}));
el('mxStageX').addEventListener('click', closeSheet);
el('mxVeil').addEventListener('click', closeSheet);
document.addEventListener('keydown', e => { if (e.key === 'Escape'){ closeSheet(); closeVote(); closeDD(); } });
const shop = el('mxShop'); if (shop) shop.addEventListener('click', e => { e.preventDefault(); const g = el('mxTower'); g.style.scrollMarginTop = '60px'; g.scrollIntoView({ block:'start' }); });

/* ── the tower (hero): hover writes the shade beside the column, click shops it ── */
(function(){
  const tw = el('mxTw'), col = el('mxTwCol'), cap = el('mxTwCap'); if (!tw || !col) return;
  const famName = key => (CFG.families.find(f => f.key === key) || {}).name || 'METALLIC';
  let capTimer = null;
  function showCap(b){
    const c = BY[b.dataset.code]; if (!c) return;
    el('mxTwCapL').textContent = `${LINE} · ${famName(b.dataset.fam)}`;
    el('mxTwCapN').textContent = c.name;
    el('mxTwCapP').textContent = c.silver ? `${money(c.price)} · REAL SILVER-WOUND · ${c.weight} G CONE`
                               : sellable(c) ? `${money(c.price)} · ${c.weight} G CONE` : 'NOT YET WOUND · VOTE FOR NEXT RUN';
    // align the caption with the hovered block (after its expansion settles)
    const place = () => { const r = b.getBoundingClientRect(), t = tw.getBoundingClientRect(); cap.style.top = (r.top - t.top + r.height / 2) + 'px'; };
    place(); clearTimeout(capTimer); capTimer = setTimeout(place, 300);
    cap.classList.add('on');
    col.querySelectorAll('.mx-tw-b').forEach(x => x.classList.toggle('on', x === b));
  }
  function hideCap(){ cap.classList.remove('on'); col.querySelectorAll('.mx-tw-b').forEach(x => x.classList.remove('on')); }
  col.querySelectorAll('.mx-tw-b').forEach(b => {
    b.addEventListener('mouseenter', () => showCap(b));
    b.addEventListener('focus', () => { col.classList.add('foil'); showCap(b); });
    b.addEventListener('blur', () => { col.classList.remove('foil'); hideCap(); });
    b.addEventListener('click', () => {
      const code = b.dataset.code, card = root.querySelector(`.mx-card[data-code="${code}"]`);
      select(code, false);
      if (card){
        const grp = card.closest('.mx-grp'); if (grp){ grp.style.scrollMarginTop = '68px'; grp.scrollIntoView({ block:'start' }); }
        openDD(card, BY[code]);
      }
    });
  });
  col.addEventListener('mouseleave', hideCap);
  // metal list on the left: hover isolates that metal in the tower, click jumps to its group
  el('mxTwFams').querySelectorAll('button').forEach(f => {
    f.addEventListener('mouseenter', () => { tw.dataset.fam = f.dataset.fam; col.classList.add('foil'); });
    f.addEventListener('mouseleave', () => { delete tw.dataset.fam; col.classList.remove('foil'); });
    f.addEventListener('click', () => { const g = el('mx-' + f.dataset.fam); if (g){ g.style.scrollMarginTop = '68px'; g.scrollIntoView({ block:'start' }); } });
  });
})();

/* ── cart ──────────────────────────────────────────────────────────── */
async function addToCart(c, qty, labelEl){
  const vid = variantOf(c);
  const orig = labelEl.textContent;
  if (!vid){ labelEl.textContent = 'UNAVAILABLE'; setTimeout(() => labelEl.textContent = orig, 1600); return; }
  if (labelEl.dataset.busy === '1') return;
  labelEl.dataset.busy = '1'; labelEl.textContent = '…';
  try {
    const r = await fetch('/cart/add.js', { method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify({ items:[{ id: vid, quantity: qty }] }) });
    labelEl.textContent = r.ok ? '✓ ADDED' : 'TRY AGAIN';
    if (r.ok){
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('#cart-count').forEach(n => n.textContent = cart.item_count);
      document.dispatchEvent(new CustomEvent('silkhee:cart:updated', { detail: cart }));
    }
  } catch(_){ labelEl.textContent = 'TRY AGAIN'; }
  setTimeout(() => { labelEl.textContent = orig; labelEl.dataset.busy = '0'; }, 1800);
}
el('mxAdd').addEventListener('click', function(){ if (CUR) addToCart(CUR, 1, this); });
el('mxVote').addEventListener('click', () => { if (CUR) openVote(CUR); });

/* ── vote modal (Formspree xbdqraql, same as the poly wall) ─────────── */
const reqOv = el('mxReq');
function openVote(c){
  reqOv.dataset.code = c.code;
  const sw = el('mxReqSw'), b = root.querySelector(`.mx-card[data-code="${c.code}"]`);
  ['--h','--hi','--lo'].forEach(v => sw.style.setProperty(v, b ? b.style.getPropertyValue(v) : c.hex));
  el('mxReqLine').textContent = `${LINE} · ${(famOf(c.code) || {}).name || 'METALLIC'}`;
  el('mxReqName').textContent = c.name;
  el('mxReqOk').style.display = 'none';
  reqOv.classList.add('open');
  if (isPhone()) closeSheet();
}
function closeVote(){ reqOv.classList.remove('open'); }
el('mxReqX').addEventListener('click', closeVote);
reqOv.addEventListener('click', e => { if (e.target === reqOv) closeVote(); });
el('mxReqBtn').addEventListener('click', function(){
  const code = reqOv.dataset.code, c = BY[code];
  const name = el('mxReqUser').value.trim(), email = el('mxReqEmail').value.trim();
  if (!email || !c){ el('mxReqEmail').focus(); return; }
  const btn = this; btn.disabled = true; btn.textContent = 'SENDING…';
  fetch('https://formspree.io/f/xbdqraql', {
    method:'POST', headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
    body: JSON.stringify({ code, shade: c.name, line: LINE, sku: c.sku, name, email, source:'metallic-vote', subject:'Metallic Vote — ' + c.name + ' (' + code + ')' })
  }).finally(() => {
    el('mxReqOk').style.display = 'block';
    btn.textContent = '✓ VOTED'; btn.disabled = false;
    const tag = root.querySelector(`.mx-card[data-code="${code}"] .mx-card-tag`);
    if (tag) tag.textContent = 'VOTED ✓';
  });
});

/* ── boot: deep link, else Antique Gold ─────────────────────────────── */
const hash = (location.hash || '').replace('#', '');
select(BY[hash] ? hash : '5577', false);
